/**
 * List header: tabs and filters.
 * Column headers are in ColumnHeadersRow (inside scroll area).
 */

import type { FC } from 'react';
import { ColumnHeadersRow } from './ColumnHeadersRow';
import { FiltersRow } from './FiltersRow';
import { TabsRow } from './TabsRow';
import type { SentSection, SortKey } from '../../shared/types';

export type { SentSection, SortKey };

export interface ListHeaderProps {
  activeTab: 'drafts' | 'sent';
  sentSection: SentSection;
  draftCount: number;
  sentCount: number;
  sentCountBySection: Record<SentSection, number>;
  showPlusOne: boolean;
  searchQuery: string;
  onSearchChange: (value: string) => void;
  onSwitchToDrafts: () => void;
  onSwitchToSent: () => void;
  onSentSectionChange?: (section: SentSection) => void;
  sortBy?: SortKey | null;
  sortDir?: 'asc' | 'desc';
  onSort?: (key: SortKey) => void;
  tagFilter?: string;
  tagFilterOptions?: string[];
  tagColors?: Record<string, string>;
  tagsLoading?: boolean;
  onTagFilterChange?: (value: string) => void;
  staticTagFilters?: string[];
  staticTagFilterOptions?: string[];
  staticTagColors?: Record<string, string>;
  onStaticTagFiltersChange?: (values: string[]) => void;
  showDragHandle?: boolean;
  showCountryFilter?: boolean;
  countryFilterOptions?: Array<{ countryKey: string; label: string }>;
  selectedCountryKeys?: string[];
  onCountryKeysChange?: (keys: string[]) => void;
  showScheduledSortToolbar?: boolean;
  scheduledSortBy?: SortKey | null;
  scheduledSortDir?: 'asc' | 'desc';
  onToggleScheduledSendTimeSort?: () => void;
  smswiserDraftsOnly?: boolean;
  onToggleSmswiserDraftsOnly?: () => void;
}

/** Renders the top bar (tabs + filters). Column headers rendered separately. */
export const ListHeader: FC<ListHeaderProps> = (props) => {
  const {
    activeTab,
    sentSection,
    draftCount,
    sentCount,
    sentCountBySection,
    showPlusOne,
    searchQuery,
    onSearchChange,
    onSwitchToDrafts,
    onSwitchToSent,
    onSentSectionChange,
    tagFilter = '',
    tagFilterOptions = [],
    tagColors,
    tagsLoading,
    onTagFilterChange,
    staticTagFilters = [],
    staticTagFilterOptions = [],
    staticTagColors,
    onStaticTagFiltersChange,
    showCountryFilter = false,
    countryFilterOptions = [],
    selectedCountryKeys = [],
    onCountryKeysChange,
    showScheduledSortToolbar = false,
    scheduledSortBy = null,
    scheduledSortDir = 'desc',
    onToggleScheduledSendTimeSort,
    smswiserDraftsOnly = false,
    onToggleSmswiserDraftsOnly,
  } = props;

  const showTagFilters = activeTab === 'drafts' && !!onTagFilterChange;
  const showStaticTagFilters = !!onStaticTagFiltersChange;

  return (
    <div className="border-b border-border/40 bg-background shadow-sm">
      <div className="flex flex-col gap-2 border-b border-border/40 bg-background/95 px-4 py-2 backdrop-blur-sm">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <TabsRow
            activeTab={activeTab}
            draftCount={draftCount}
            sentCount={sentCount}
            sentCountBySection={sentCountBySection}
            sentSection={sentSection}
            onSwitchToDrafts={onSwitchToDrafts}
            onSwitchToSent={onSwitchToSent}
            onSentSectionChange={onSentSectionChange}
            showPlusOne={showPlusOne}
          />
          <FiltersRow
            searchQuery={searchQuery}
            onSearchChange={onSearchChange}
            tagFilter={tagFilter}
            tagFilterOptions={tagFilterOptions}
            tagColors={tagColors}
            tagsLoading={tagsLoading}
            onTagFilterChange={onTagFilterChange}
            staticTagFilters={staticTagFilters}
            staticTagFilterOptions={staticTagFilterOptions}
            staticTagColors={staticTagColors}
            onStaticTagFiltersChange={onStaticTagFiltersChange}
            showTagFilters={showTagFilters || showStaticTagFilters}
            showCountryFilter={showCountryFilter}
            countryFilterOptions={countryFilterOptions}
            selectedCountryKeys={selectedCountryKeys}
            onCountryKeysChange={onCountryKeysChange}
            showScheduledSortToolbar={showScheduledSortToolbar}
            scheduledSortBy={scheduledSortBy}
            scheduledSortDir={scheduledSortDir}
            onToggleScheduledSendTimeSort={onToggleScheduledSendTimeSort}
            showSmswiserDraftsFilter={activeTab === 'drafts'}
            smswiserDraftsOnly={smswiserDraftsOnly}
            onToggleSmswiserDraftsOnly={onToggleSmswiserDraftsOnly}
          />
        </div>
      </div>
    </div>
  );
};

/** Column headers row - use inside scroll area for horizontal alignment. */
export { ColumnHeadersRow };
